import { useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { getStatusSettings, deleteStatusSetting } from "api/apiService";
import { notifyCenter } from "utils/toast";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

import { statusLabel as labelForStatus, statusColor as chipColorForStatus } from "utils/statusLabels";

function StatusSettings() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function fetchData(nextPage = page) {
    setLoading(true);
    setError(null);
    try {
      const res = await getStatusSettings({ limit: pageSize, page_number: nextPage });
      if (res?.status) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotalRecords(res?.data_info?.total_records ?? 0);
        setTotalPages(res?.data_info?.total_pages ?? 1);
      } else {
        setError(res?.message || "Kunne ikke hente statusinnstillinger");
        setRows([]);
      }
    } catch (e) {
      setError(e?.message || "Nettverksfeil");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const offset = useMemo(() => (page - 1) * pageSize, [page]);

  function openEdit(id) {
    navigate(`/status-settings/edit-statusSettings/${id}`);
  }

  function handleDeleteClick(id) {
    setDeleteId(id);
    setConfirmOpen(true);
  }

  async function confirmDelete() {
    setConfirmOpen(false);
    if (!deleteId) return;
    setLoading(true);
    try {
      const res = await deleteStatusSetting(deleteId);
      if (res?.status) {
        notifyCenter("success", res.message || "Slettet", { duration: 2000 });
        setDeleteId(null);
        fetchData(page);
      } else {
        notifyCenter("error", res?.message || "Kunne ikke slette", { duration: 3000 });
      }
    } catch (err) {
      notifyCenter("error", "Kunne ikke slette statusinnstillingen", { duration: 3000 });
      console.error("Delete error:", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout>
      <ConfirmDeleteModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />

      <SoftBox py={3} px={{ xs: 1, md: 2 }}>
        <Card sx={{ position: "relative" }}>
          {/* Header */}
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            p={3}
          >
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="bold">
                Statusinnstillinger
              </SoftTypography>
              <SoftTypography variant="button" color="text">
                Konfigurer hvordan dato og pris vises per ordrestatus
              </SoftTypography>
            </SoftBox>
            <SoftButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => navigate("/status-settings/create-statusSettings")}
            >
              Ny innstilling
            </SoftButton>
          </SoftBox>

          <Divider sx={{ my: 0 }} />

          {error && (
            <SoftBox px={3} py={2}>
              <SoftTypography variant="button" color="error">
                {error}
              </SoftTypography>
            </SoftBox>
          )}

          {/* Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                minWidth: 960,
                tableLayout: "fixed",
                // Override Soft UI theme forcing thead to display:block
                "& thead": {
                  display: "table-header-group",
                  padding: 0,
                  borderRadius: 0,
                },
                "& tbody": { display: "table-row-group" },
                "& tr": { display: "table-row" },
                "& td, & th": {
                  display: "table-cell",
                  padding: "12px 14px",
                  borderBottom: (t) => `1px solid ${t.palette.grey[200]}`,
                  fontSize: 13,
                  verticalAlign: "middle",
                },
                "& thead th": {
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  fontSize: 11,
                  letterSpacing: 0.4,
                  whiteSpace: "nowrap",
                },
                "& tbody tr": {
                  cursor: "pointer",
                },
                "& tbody tr:hover": {
                  backgroundColor: (t) => t.palette.grey[50],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "8%" }}>ID</TableCell>
                  <TableCell sx={{ width: "20%" }}>Status</TableCell>
                  <TableCell sx={{ width: "10%" }} align="center">Vis dato</TableCell>
                  <TableCell sx={{ width: "auto" }}>Datotekst</TableCell>
                  <TableCell sx={{ width: "10%" }} align="center">Vis pris</TableCell>
                  <TableCell sx={{ width: "auto" }}>Pristekst</TableCell>
                  <TableCell sx={{ width: "10%" }} align="right">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow sx={{ cursor: "default !important" }}>
                    <TableCell colSpan={7} sx={{ py: 6 }}>
                      <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <InboxOutlinedIcon
                          sx={(t) => ({ fontSize: 48, color: t.palette.grey[400] })}
                        />
                        <SoftTypography variant="body2" color="secondary">
                          Ingen statusinnstillinger – opprett den første
                        </SoftTypography>
                        <SoftButton
                          variant="gradient"
                          color="info"
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => navigate("/status-settings/create-statusSettings")}
                          sx={{ mt: 1 }}
                        >
                          Ny innstilling
                        </SoftButton>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                )}

                {rows.map((r) => {
                  const showDate = String(r.show_date) === "1";
                  const showPrice = String(r.show_price) === "1";
                  return (
                    <TableRow key={r.id} hover onClick={() => openEdit(r.id)}>
                      <TableCell sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                        #{r.id}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={labelForStatus(r.status)}
                          color={chipColorForStatus(r.status)}
                          size="small"
                          sx={{ fontWeight: 600, height: 22 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={showDate ? "Aktivert" : "Deaktivert"}
                          color={showDate ? "success" : "default"}
                          size="small"
                          variant={showDate ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: (t) => t.palette.text.secondary,
                          fontSize: 12,
                        }}
                      >
                        {r.date_text_status || "—"}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={showPrice ? "Aktivert" : "Deaktivert"}
                          color={showPrice ? "success" : "default"}
                          size="small"
                          variant={showPrice ? "filled" : "outlined"}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: (t) => t.palette.text.secondary,
                          fontSize: 12,
                        }}
                      >
                        {r.price_text_status || "—"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Rediger">
                          <IconButton size="small" onClick={() => openEdit(r.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Slett">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(r.id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer pagination */}
          {rows.length > 0 && (
            <>
              <Divider sx={{ my: 0 }} />
              <SoftBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                p={3}
              >
                <SoftTypography variant="caption" color="secondary">
                  Viser {rows.length ? offset + 1 : 0}
                  {"–"}
                  {offset + rows.length} av {totalRecords}
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || loading}
                  >
                    Forrige
                  </SoftButton>
                  <SoftTypography variant="caption" color="text" px={1}>
                    Side {page} / {totalPages}
                  </SoftTypography>
                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || loading}
                  >
                    Neste
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </>
          )}

          {loading && (
            <SoftBox
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
                borderRadius: "inherit",
              }}
            >
              <CircularProgress color="info" size={36} />
            </SoftBox>
          )}
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default StatusSettings;
